/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
	AppBar,
	Badge,
	Button,
	IconButton,
	Toolbar,
	Hidden,
	Input,
	colors,
	Popper,
	Paper,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ClickAwayListener,
} from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import InputIcon from '@mui/icons-material/Input';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';

import useRouter from 'utils/useRouter';
import { PricingModal, NotificationsPopover } from 'components';
import * as actions from 'actions';

const useStyles = makeStyles((theme) => ({
	root: {
		boxShadow: 'none',
	},
	flexGrow: {
		flexGrow: 1,
	},
	search: {
		backgroundColor: 'rgba(255,255,255, 0.1)',
		borderRadius: 4,
		flexBasis: 300,
		height: 36,
		padding: theme.spacing(0, 2),
		display: 'flex',
		alignItems: 'center',
	},
	searchIcon: {
		marginRight: theme.spacing(2),
		color: 'inherit',
	},
	searchInput: {
		flexGrow: 1,
		color: 'inherit',
		'& input::placeholder': {
			opacity: 1,
			color: 'inherit',
		},
	},
	searchPopper: {
		zIndex: theme.zIndex.appBar + 100,
	},
	searchPopperContent: {
		marginTop: theme.spacing(1),
	},
	trialButton: {
		marginLeft: theme.spacing(2),
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
	trialIcon: {
		marginRight: theme.spacing(1),
	},
	notificationsButton: {
		marginLeft: theme.spacing(1),
	},
	notificationsBadge: {
		backgroundColor: colors.orange[600],
	},
	logoutButton: {
		marginLeft: theme.spacing(1),
	},
	logoutIcon: {
		marginRight: theme.spacing(1),
	},
}));

const TopBar = (props) => {
	const { onOpenNavBarMobile, className, ...rest } = props;

	const classes = useStyles();
	const { history } = useRouter();
	const searchRef = useRef(null);
	const dispatch = useDispatch();
	const notificationsRef = useRef(null);
	const [pricingModalOpen, setPricingModalOpen] = useState(false);
	const [openSearchPopover, setOpenSearchPopover] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [openNotifications, setOpenNotifications] = useState(false);

	const notifications = useSelector((state) => state.session.notifications, shallowEqual);
	useEffect(() => {
		let mounted = true;
		return () => {
			mounted = false;
		};
	}, []);

	const handleLogout = () => {
		dispatch(actions.logout());
		history.push('/auth/login');
	};

	const handlePricingOpen = () => {
		setPricingModalOpen(true);
	};

	const handlePricingClose = () => {
		setPricingModalOpen(false);
	};

	const handleNotificationsOpen = () => {
		setOpenNotifications(true);
	};

	const handleNotificationsClose = () => {
		setOpenNotifications(false);
	};

	const handleSearchChange = (event) => {
		setSearchValue(event.target.value);

		if (event.target.value) {
			if (!openSearchPopover) {
				setOpenSearchPopover(true);
			}
		} else {
			setOpenSearchPopover(false);
		}
	};

	const handleSearchPopverClose = () => {
		setOpenSearchPopover(false);
	};

	const popularSearches = [
		'Devias React Dashboard',
		'Devias',
		'Admin Pannel',
		'Project',
		'Pages',
	];

	return (
		<AppBar
			{...rest}
			className={clsx(classes.root, className)}
			color="primary"
		>
			<Toolbar>
				<RouterLink to="/">
					<img alt="Logo" src="./images/logos/logo.png" style={{ maxHeight: '45px', borderRadius: '50%' }} />
				</RouterLink>
				<RouterLink to="/">
					<h2 style={{ color: 'white', fontFamily: 'Roboto' }}>&nbsp;Merged CO UK</h2>
				</RouterLink>
				<div className={classes.flexGrow} />
				{/*  <Hidden smDown>
				   <div className={classes.search} ref={searchRef}>
						<SearchIcon className={classes.searchIcon} />
						<Input
							className={classes.searchInput}
							disableUnderline
							onChange={handleSearchChange}
							placeholder="Search people &amp; places"
							value={searchValue}
						/>
					</div>
					<Popper
						anchorEl={searchRef.current}
						className={classes.searchPopper}
						open={openSearchPopover}
						transition>
						<ClickAwayListener
							onClickAway={handleSearchPopverClose}>
							<Paper
								className={classes.searchPopperContent}
								elevation={3}>
								<List>
									{popularSearches.map((search) => (
										<ListItem
											button
											key={search}
											onClick={handleSearchPopverClose}>
											<ListItemIcon>
												<SearchIcon />
											</ListItemIcon>
											<ListItemText primary={search} />
										</ListItem>
									))}
								</List>
							</Paper>
						</ClickAwayListener>
					</Popper>
				</Hidden> */}
				<Hidden lgDown>
					<IconButton
						className={classes.notificationsButton}
						color="inherit"
						onClick={handleNotificationsOpen}
						ref={notificationsRef}
						size="large"
					>
						<Badge
							badgeContent={notifications.filter(i => !i.readed).length}
							classes={{ badge: classes.notificationsBadge }}
							variant="dot"
						>
							<NotificationsIcon />
						</Badge>
					</IconButton>
					<Button
						className={classes.logoutButton}
						color="inherit"
						onClick={handleLogout}
					>
						<InputIcon className={classes.logoutIcon} />
						Sign out
					</Button>
				</Hidden>
				<Hidden lgUp>
					<IconButton color="inherit" onClick={onOpenNavBarMobile} size="large">
						<MenuIcon />
					</IconButton>
				</Hidden>
			</Toolbar>
			<PricingModal
				onClose={handlePricingClose}
				open={pricingModalOpen}
			/>
			<NotificationsPopover
				anchorEl={notificationsRef.current}
				notifications={notifications}
				onClose={handleNotificationsClose}
				open={openNotifications}
			/>
		</AppBar>
	);
};

TopBar.propTypes = {
	className: PropTypes.string,
	onOpenNavBarMobile: PropTypes.func,
};

export default TopBar;
